import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Info, FileText, MessageCircle, ChevronRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { useSEO } from "@/hooks/useSEO";
import { useContact } from "@/context/ContactContext";
import { api } from "@/lib/api";


export default function PriceGuide() {
  useSEO({
    title: "Renovation Price List Singapore — Hacking, Tiling, Carpentry & More",
    description: "WAAN METAL's full renovation price list: itemised Singapore rates for hacking, tiling, carpentry, plumbing, electrical, ceiling & partition, bathroom packages and coordination. Metal & commercial works by quotation.",
  });
  const contact = useContact();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(params.get("cat") || "All");
  const waNum = (contact.whatsapp || "").replace(/[^0-9]/g, "");
  const waHref = waNum ? `https://wa.me/${waNum}` : "/request-quotation";

  useEffect(() => {
    api.get("/price-guide").then(({ data }) => {
      data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setItems(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const c = params.get("cat");
    if (c) setActive(c);
  }, [params]);

  const categories = useMemo(() => {
    const seen = [];
    items.forEach((i) => { if (!seen.includes(i.category)) seen.push(i.category); });
    return seen;
  }, [items]);

  const selectCat = (c) => {
    setActive(c);
    if (c === "All") setParams({}); else setParams({ cat: c });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filtered = useMemo(() => {
    let list = items;
    if (active !== "All") list = list.filter((i) => i.category === active);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((i) => `${i.item} ${i.category} ${i.scope || ""} ${i.inclusions || ""}`.toLowerCase().includes(q));
    }
    return list;
  }, [items, active, query]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((i) => { (g[i.category] = g[i.category] || []).push(i); });
    return g;
  }, [filtered]);

  const priceOf = (it) => it.price_range || it.starting_price || null;

  return (
    <Layout>
      <section className="bg-[#0A1128] py-12 lg:py-16" data-testid="price-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-2">Renovation Price List</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">Transparent Singapore renovation rates</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Itemised, management-approved selling prices. Search or browse by category. Final pricing is confirmed in a written quotation after a site assessment.</p>
          <div className="mt-6 relative max-w-xl">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search rates — e.g. wardrobe, wall tiling, power point…"
              data-testid="price-search"
              className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white text-slate-900 outline-none focus:ring-2 focus:ring-[#B87333]"
            />
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[260px_1fr] gap-8">
          {/* Sidebar category nav */}
          <aside className="lg:sticky lg:top-24 self-start" data-testid="price-category-nav">
            <p className="eyebrow mb-3">Categories</p>
            <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0">
              <button onClick={() => selectCat("All")} data-testid="price-cat-all" className={`shrink-0 text-left px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${active === "All" ? "bg-[#0A1128] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>All Categories</button>
              {categories.map((c) => (
                <button key={c} onClick={() => selectCat(c)} data-testid={`price-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`shrink-0 text-left px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${active === c ? "bg-[#B87333] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{c}</button>
              ))}
            </div>
            <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-5 hidden lg:block">
              <h3 className="font-semibold text-slate-900 text-sm">Need a firm quote?</h3>
              <p className="mt-1.5 text-xs text-slate-600">Rates here are indicative. Get an itemised quotation for your exact scope.</p>
              <Link to="/request-quotation" data-testid="price-sidebar-quote" className="mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#B87333] text-white text-sm font-bold">Request a Quote</Link>
              <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" className="mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm font-semibold"><MessageCircle size={15} /> WhatsApp</a>
            </div>
          </aside>

          {/* Tables */}
          <div data-testid="price-content">
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 flex gap-3 text-sm text-blue-900 mb-6">
              <Info size={18} className="shrink-0 mt-0.5 text-blue-500" />
              <p>Prices are indicative and may vary with scope, materials, site conditions and approvals. GST and PUB submission fees apply where noted. Items marked <strong>Request Quotation</strong> are priced individually. Nothing here is a binding quotation.</p>
            </div>

            {Object.keys(grouped).length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="price-no-results">No rates match your search.</div>
            ) : (
              Object.entries(grouped).map(([cat, rows]) => (
                <div key={cat} className="mb-10" data-testid={`price-group-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2"><ChevronRight size={18} className="text-[#B87333]" /> {cat}</h2>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-left min-w-[640px]">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-mono-label">
                        <tr>
                          <th className="px-5 py-3.5">Item</th>
                          <th className="px-5 py-3.5">Unit</th>
                          <th className="px-5 py-3.5">Price</th>
                          <th className="px-5 py-3.5">Scope / Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {rows.map((it) => (
                          <tr key={it.id} className="hover:bg-slate-50/60 align-top" data-testid={`price-row-${it.id}`}>
                            <td className="px-5 py-4 font-medium text-slate-900">{it.item}</td>
                            <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">{it.unit || "—"}</td>
                            <td className="px-5 py-4 whitespace-nowrap">
                              {priceOf(it) ? (
                                <span className="font-bold text-[#0A1128]">{priceOf(it)}</span>
                              ) : (
                                <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#B87333] font-mono-label uppercase tracking-wider"><FileText size={13} /> Request Quotation</a>
                              )}
                              {it.gst && <span className="block text-[11px] text-slate-400 mt-0.5">{it.gst}</span>}
                            </td>
                            <td className="px-5 py-4 text-sm text-slate-500 max-w-sm">
                              {it.scope && <span className="block">{it.scope}</span>}
                              {it.inclusions && <span className="block"><span className="text-slate-400">Incl:</span> {it.inclusions}</span>}
                              {it.exclusions && <span className="block"><span className="text-slate-400">Excl:</span> {it.exclusions}</span>}
                              {!it.scope && !it.inclusions && !it.exclusions && "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}

            <div className="rounded-xl bg-[#0A1128] p-7 flex flex-col sm:flex-row items-center justify-between gap-5">
              <div><h3 className="text-lg font-bold text-white">Ready for an exact quotation?</h3><p className="mt-1 text-slate-300 text-sm">Tell us your scope and we'll itemise it for you.</p></div>
              <div className="flex gap-3 shrink-0">
                <Link to="/request-quotation" data-testid="price-quote-cta" className="px-6 py-3 rounded-full bg-[#B87333] text-white font-bold hover:bg-[#a9652c] transition-colors">Request a Quote</Link>
                <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white font-semibold hover:bg-white/10"><MessageCircle size={17} /> WhatsApp</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
